import type { AnalyticsConfig } from './types'

// ---------------------------------------------------------------------------
// Funnel analytics (§9). All IDs are config-driven and EVERYTHING is a no-op
// when unset — so the mock/dev environment emits nothing and unbranded builds
// stay silent. A single track(event, payload) helper fans out to GA / FB / TikTok.
// ---------------------------------------------------------------------------

type TrackPayload = Record<string, unknown>

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...a: unknown[]) => void; queue?: unknown[] }
    ttq?: { track: (event: string, payload?: TrackPayload) => void; load?: (id: string) => void; page?: () => void }
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] }
    _hjSettings?: { hjid: number; hjsv: number }
  }
}

let cfg: AnalyticsConfig = {}
let initialized = false

/** Call once after config loads. Injects vendor scripts only for IDs that are set. */
export function initAnalytics(config: AnalyticsConfig | undefined): void {
  if (initialized) return
  initialized = true
  cfg = config || {}

  if (cfg.ga_measurement_id) loadGa(cfg.ga_measurement_id)
  if (cfg.facebook_pixel_id) loadFbq(cfg.facebook_pixel_id)
  if (cfg.tiktok_pixel_id) loadTtq(cfg.tiktok_pixel_id)
  if (cfg.hotjar_id) loadHotjar(cfg.hotjar_id)
}

/** Generic event — fires a GA custom event when GA is configured; otherwise no-op. */
export function track(event: string, payload: TrackPayload = {}): void {
  try {
    if (cfg.ga_measurement_id && typeof window.gtag === 'function') {
      window.gtag('event', event, payload)
    }
  } catch {
    /* analytics must never break the flow */
  }
}

/** Conversion event (§9): GA `booked` + Facebook `Schedule` + TikTok `PlaceAnOrder`. */
export function trackBooked(payload: TrackPayload = {}): void {
  track('booked', payload)
  try {
    if (cfg.facebook_pixel_id && typeof window.fbq === 'function') {
      window.fbq('track', 'Schedule', payload)
    }
  } catch {
    /* ignore */
  }
  try {
    if (cfg.tiktok_pixel_id && window.ttq && typeof window.ttq.track === 'function') {
      window.ttq.track('PlaceAnOrder', payload)
    }
  } catch {
    /* ignore */
  }
}

// ---- Vendor loaders (only invoked when an ID is present) ----------------------

function loadGa(id: string): void {
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(s)
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', id)
}

function loadFbq(id: string): void {
  /* Standard Facebook Pixel bootstrap (vendor shim — typed loosely on purpose). */
  if (window.fbq) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fbq: any = function (...args: unknown[]) {
    fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args)
  }
  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  window.fbq = fbq
  const s = document.createElement('script')
  s.async = true
  s.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(s)
  fbq('init', id)
  fbq('track', 'PageView')
}

/**
 * Hotjar (heatmaps + session recordings). Same bootstrap as the vendor snippet,
 * expressed in the loader style used above so it stays config-gated: no id in
 * the config → no script, so the mock/dev environment records nothing.
 *
 * PHI: this is a patient-facing healthcare flow. Every field that collects
 * patient information carries `data-hj-suppress` (see DetailsForm) so its
 * contents are masked before anything leaves the browser. Keep that in place —
 * and note Hotjar does not offer a BAA on standard plans, so nothing
 * identifiable should be allowed to reach it.
 */
function loadHotjar(id: number | string): void {
  if (window.hj) return
  const hjid = Number(id)
  if (!Number.isFinite(hjid) || hjid <= 0) return
  const hjsv = 6
  const hj: Window['hj'] = Object.assign(
    function (...args: unknown[]) {
      ;(hj!.q = hj!.q || []).push(args)
    },
    { q: [] as unknown[] }
  )
  window.hj = hj
  window._hjSettings = { hjid, hjsv }
  const s = document.createElement('script')
  s.async = true
  s.src = `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=${hjsv}`
  document.head.appendChild(s)
}

function loadTtq(id: string): void {
  const s = document.createElement('script')
  s.async = true
  s.src = `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(id)}`
  document.head.appendChild(s)
  // Minimal queue shim so calls before load don't throw.
  if (!window.ttq) {
    const queue: Array<[string, TrackPayload?]> = []
    window.ttq = {
      track: (event: string, payload?: TrackPayload) => queue.push([event, payload])
    }
  }
}
